import React, { useState, useEffect } from "react";
import { documentTypesAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  FolderOpen,
  Plus,
  Trash2,
  Loader2,
  Edit,
  X,
} from "lucide-react";

const COLOR_OPTIONS = [
  "#2D7BB7",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EF4444",
  "#EC4899",
  "#06B6D4",
  "#84CC16",
];

export function DocumentTypesPage() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editType, setEditType] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#2D7BB7",
  });

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      const response = await documentTypesAPI.getAll();
      setTypes(response.data);
    } catch (error) {
      showMessage("Errore nel caricamento delle tipologie", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showMessage("Inserisci il nome della tipologia", "error");
      return;
    }

    setSaving(true);
    try {
      if (editType) {
        await documentTypesAPI.update(editType.id, formData);
        showMessage("Tipologia aggiornata");
      } else {
        await documentTypesAPI.create(formData);
        showMessage("Tipologia creata");
      }
      setShowForm(false);
      setEditType(null);
      setFormData({ name: "", description: "", color: "#2D7BB7" });
      loadTypes();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante il salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (type) => {
    setEditType(type);
    setFormData({
      name: type.name,
      description: type.description || "",
      color: type.color || "#2D7BB7",
    });
    setShowForm(true);
  };

  const handleDeleteClick = (typeId) => {
    setDeleteId(typeId);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await documentTypesAPI.delete(deleteId);
      showMessage("Tipologia eliminata");
      loadTypes();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante l'eliminazione", "error");
    } finally {
      setDeleteId(null);
      setShowDeleteConfirm(false);
    }
  };

  const openNewForm = () => {
    setEditType(null);
    setFormData({ name: "", description: "", color: "#2D7BB7" });
    setShowForm(true);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="fixed inset-0 bg-black/50" 
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative z-50 bg-card border border-border rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Conferma eliminazione
            </h3>
            <p className="text-muted-foreground mb-6">
              Sei sicuro di voler eliminare questa tipologia?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-border rounded-md text-foreground hover:bg-muted transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                data-testid="confirm-delete-type"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="fixed inset-0 bg-black/50" 
            onClick={() => setShowForm(false)}
          />
          <div className="relative z-50 bg-card border border-border rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-heading font-bold text-foreground">
                  {editType ? "Modifica Tipologia" : "Nuova Tipologia"}
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowForm(false)}
                >
                  <X size={20} />
                </Button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Es: Fattura, Contratto..."
                    className="bg-background border-input text-foreground"
                    data-testid="type-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Descrizione</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Descrizione opzionale"
                    className="bg-background border-input text-foreground"
                    data-testid="type-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Colore</Label>
                  <div className="flex gap-2 flex-wrap">
                    {COLOR_OPTIONS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-8 h-8 rounded-full transition-transform ${
                          formData.color === color
                            ? "ring-2 ring-foreground ring-offset-2 ring-offset-card scale-110"
                            : "hover:scale-110"
                        }`}
                        style={{ backgroundColor: color }}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="border-border text-foreground"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                    data-testid="save-type-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : editType ? (
                      "Salva"
                    ) : (
                      "Crea"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">
            Tipologie Documento
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci le tipologie di documenti protocollabili
          </p>
        </div>
        <Button
          onClick={openNewForm}
          className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white shadow-md"
          data-testid="add-type-btn"
        >
          <Plus className="mr-2" size={18} />
          Nuova Tipologia
        </Button>
      </div>

      {/* Types List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
            <FolderOpen size={20} className="text-[#2D7BB7]" />
            Tipologie Disponibili
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-16 bg-muted rounded-lg" />
              ))}
            </div>
          ) : types.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FolderOpen size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessuna tipologia configurata</p>
            </div>
          ) : (
            <div className="space-y-3">
              {types.map((type) => (
                <div
                  key={type.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
                  data-testid={`type-item-${type.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: type.color }}
                    />
                    <div>
                      <p className="text-foreground font-medium">
                        {type.name}
                      </p>
                      {type.description && (
                        <p className="text-sm text-muted-foreground">{type.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(type)}
                      className="text-muted-foreground hover:text-[#2D7BB7] hover:bg-blue-50"
                      data-testid={`edit-type-${type.id}`}
                    >
                      <Edit size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(type.id)}
                      className="text-muted-foreground hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-type-${type.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
